import React, {useState, useRef, useEffect} from 'react';
import axios from "axios";
import {toast} from "react-toastify";
import _ from "lodash";
import Input from "../../forms/Input";
import AssignProducts from "./products/AssignProducts";
import Button from "../../Button";
import {doubleBookedHelper} from "../../../helpers/DoubleBookedHelper";
import { DATE_FORMATS } from '../../../constants/DateFormats';
import { de } from 'date-fns/locale';
import ReactDatePicker from 'react-datepicker';

const DELIVERY_DEFAULTS = {
    id:                         '',
    delivery_time:              '',
    devliery_date:              '',
    user_id:                    'draft',
    status:                     '',
    notes:                      '',
    product_ids:                [],
    has_overridden_products:    false,
}

const CreateEditDelivery = (props) => {
    const {
        jobId       = null,
        orderId     = null,
        deliveryId  = null,
        onCancel    = () => {},
        onComplete  = () => {},
    } = props;

    const prepareDefaultValues = () => {
        const defaultDelivery = DELIVERY_DEFAULTS;

        if (!!deliveryId && deliveryId !== "") {
            defaultDelivery.id = parseInt(deliveryId);
        }

        return defaultDelivery;
    }

    const [submitting, setSubmitting]               = useState(false);
    const [deliveryStatuses, setDeliveryStatuses]   = useState([]);
    const [users, setUsers]                         = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [job, setJob]                             = useState(null);
    const [delivery, setDelivery]                   = useState(prepareDefaultValues());
    const [notes, setNotes]                         = useState(null);
    const [errors, setErrors]                       = useState(null);

    useEffect(() => {
        load();
    }, [jobId, orderId, deliveryId]);

    const load = () => {
        axios.get(route('web-api.job.order.delivery.config', {job: jobId, order: orderId, delivery_id: deliveryId, notes: notes}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.job) {
                        setJob(response.data.job);
                    }

                    if (!!response.data.products) {
                        setAvailableProducts(response.data.products);
                    }

                    if (!!response.data.delivery_statuses) {
                        setDeliveryStatuses(response.data.delivery_statuses);
                    }

                    if (!!response.data.users) {
                        setUsers(response.data.users);
                    }

                    if (!!response.data.delivery) {
                        setDelivery(response.data.delivery)
                    } else {
                        setDelivery(DELIVERY_DEFAULTS);
                    }

                    if (!!response.data.notes) {
                        setNotes(response.data.notes);
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
                setSubmitting(false);
            });
    }

    useEffect(() => {
        if (!!submitting) {
            submit();
        }
    }, [submitting]);

    const reset = () => {
        setDelivery(prepareDefaultValues());

        setSubmitting(false);
        setErrors(null);
    }

    const payload = () => {
        return {
            method: !!deliveryId ? 'patch' : 'post',
            url:    !!deliveryId ? route('web-api.job.order.delivery.update', {job: jobId, order: orderId, delivery: deliveryId, notes: notes}).toString() : route('web-api.job.order.delivery.store', {job: jobId, order: orderId}).toString(),
            data:   {
                ...delivery,
            },
        }
    }

    const submit = () => {
        const data = payload();
        axios(data)
            .then((response) => {
                reset();

                if (!!response && !!response.data && !!response.data.double_booked) {
                    toast.warning(doubleBookedHelper(response));
                }

                onComplete(response.data.delivery_id);
            })
            .catch((error) => {
                console.log(error);
                if (!!error.response.data && !!error.response.data.errors) {
                    setErrors(error.response.data.errors);
                    toast.error(<div>{!!error.response.data.message ? error.response.data.message : "An error occurred"}</div>);
                }

                setSubmitting(false);
            });
    }

    const handleClose = () => {
        if (!submitting) {
            reset();
            onCancel();
        }
    }

    const requiredAssignee = (status) => {
        return !(status == "pending_collection" || status == "collected");
    }

    const onChange = (field, value) => {
        const clone = _.cloneDeep(delivery);

        clone[field] = value;

        if (field == "status" && !requiredAssignee(value)) {
            clone.user_id = null;
        }

        setDelivery(clone);
    }

    return (
        <React.Fragment>
            <div className="row m-0 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"delivery_date"}
                        label={"Delivery Date"}
                        required={true}
                        type={"date"}
                        value={delivery.delivery_date}
                        disabled={submitting}
                        onChange={(value) => onChange("delivery_date", value)}
                        error={!!errors && !!errors.delivery_date ? errors.delivery_date : null}
                    />
                </div>
                <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                    <Input
                        name={"delivery_time"}
                        label={"Delivery Time"}
                        required={true}
                        type={"date"}
                        showTimeSelect
                        showTimeSelectOnly
                        dateFormat={DATE_FORMATS.HOUR_MINUTE}
                        timeIntervals={30}
                        value={delivery.delivery_time}
                        disabled={submitting}
                        onChange={(value) => onChange("delivery_time", value)}
                        error={!!errors && !!errors.delivery_time ? errors.delivery_time : null}
                    />
                </div>
            </div>
            <div className="row m-0 mb-4 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"status"}
                        label={"Delivery Status"}
                        required={true}
                        type={"searchable-select"}
                        options={deliveryStatuses}
                        value={delivery.status}
                        disabled={submitting}
                        onChange={(value) => onChange("status", value)}
                        error={!!errors && !!errors.status ? errors.status : null}
                    />
                </div>
                <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                    <Input
                        key={delivery.user_id}
                        name={"user_id"}
                        label={"Assign Delivery"}
                        required={requiredAssignee(delivery.status)}
                        type={"searchable-select"}
                        options={users}
                        value={delivery.user_id}
                        disabled={submitting || !requiredAssignee(delivery.status)}
                        onChange={(value) => onChange("user_id", value)}
                        error={!!errors && !!errors.user_id ? errors.user_id : null}
                    />
                </div>
                <div className="col-12 m-0 p-0 bespoke-input">
                    <Input
                        name={"notes"}
                        label={"Notes"}
                        required={false}
                        type={"textarea"}
                        value={delivery.notes}
                        disabled={submitting}
                        onChange={(value) => onChange("notes", value)}
                        error={!!errors && !!errors.notes ? errors.notes : null}
                    />
                </div>
            </div>

            <div className="row m-0 mb-4 p-0 pb-2 border-bottom">
                <div className="col-12 m-0 p-0">
                    <AssignProducts
                        deliveryId={deliveryId}
                        job={!!job ? job : null}
                        hasOverriddenProducts={!!delivery ? delivery.has_overridden_products : false}
                        availableProducts={availableProducts}
                        productIds={delivery.product_ids}
                        onChange={(value) => onChange("product_ids", value)}
                        disabled={submitting}
                        errors={errors}
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex justify-content-start align-items-center">
                        <Button
                            onClick={() => setSubmitting(true)}
                            label={!!deliveryId ? "Save Changes" : "Create"}
                            classes={"btn btn-eh-lilac m-0 mr-4"}
                        />
                        <Button
                            onClick={() => handleClose()}
                            label={"Cancel"}
                            classes={"btn btn-outline-eh-off-black m-0 mr-4"}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default CreateEditDelivery;
